import { ReadonlyArray, pipe } from "@morphism/fp";
import { useDebounceCallback } from "@react-hook/debounce";
import { Select as AntSelect } from "antd";
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useState } from "react";
import styled from "styled-components";

import { Branch } from "~/Branch";
import * as Color from "~/Color";
import * as Data from "~/Data";
import { Status } from "~/Driver";
import { Form } from "~/Form";
import { Input as TextInput } from "~/Input";
import * as Jobs from "~/Job";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import * as WC from "~/WorkCode";
import { WorkOrder as GenericWorkOrder, WorkOrders } from "~/WorkOrder";

import { State } from "./State";

export type Fields =
  | "driverStatus"
  | "startTime"
  | "endTime"
  | "location"
  | "workOrder"
  | "workOrder"
  | "workCodes"
  | "job"
  | "notes";

export namespace DriverStatus {
  export const Select = ({
    status,
    allowedStatuses,
    onSelect,
  }: {
    readonly status: Status.Status;
    readonly allowedStatuses: readonly Status.Status[];
    readonly onSelect: (status: Status.Status) => void;
  }) => {
    const { Option } = AntSelect;
    return (
      <Form.Field.Vertical names={["driverStatus"]} label="Driver status">
        <Container>
          <AntSelect
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            value={status}
            onChange={(status) => onSelect(status)}
          >
            {allowedStatuses.map((status) => (
              <Option key={status} value={status}>
                {Status.toString(status)}
              </Option>
            ))}
          </AntSelect>
        </Container>
      </Form.Field.Vertical>
    );
  };
}

export const fromStatus = (status: string): Status.Status => {
  switch (status) {
    case Status.toString(Status.on()):
      return Status.on();
    case Status.toString(Status.off()):
      return Status.off();
    case Status.toString(Status.drive()):
      return Status.drive();
    case Status.toString(Status.yardMove()):
      return Status.yardMove();
    case Status.toString(Status.personalUse()):
      return Status.personalUse();
    case Status.toString(Status.sleeperBerth()):
      return Status.sleeperBerth();
    default:
      return Status.off();
  }
};
export namespace Location {
  export const Input = ({
    location,
    errors,
    onChange,
  }: {
    readonly location: string;
    readonly errors?: Form.Field.ValidationErrors<Fields>;
    readonly onChange: (location: string) => void;
  }) => {
    const [locationValue, setLocationValue] = useDebounceTextInput(
      location,
      onChange
    );

    return (
      <Form.Field.Vertical
        names={["location"]}
        label="Location"
        isRequired
        errors={errors}
      >
        <TextInput
          required
          placeholder="Enter a location..."
          value={locationValue}
          onChange={({ target: { value: location } }) =>
            setLocationValue(location)
          }
          onBlur={({ target: { value: location } }) => onChange(location)}
        />
      </Form.Field.Vertical>
    );
  };
}

export namespace Job {
  export const Select = ({
    job,
    onChange,
    disabled = false,
  }: {
    readonly job: Data.Reference.Reference | undefined;
    readonly onChange: (job: Data.Reference.Reference | undefined) => void;
    readonly disabled: boolean;
  }) => {
    const { Option } = AntSelect;
    return (
      <Form.Field.Vertical names={["job"]} label="Job">
        <Container>
          <AntSelect
            style={{ width: "100%" }}
            showSearch
            allowClear={true}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={disabled}
            value={job?.id}
            placeholder="Select a job..."
            onChange={(value) =>
              value ? onChange({ id: `${value}` }) : onChange(undefined)
            }
          >
            {pipe(
              Jobs.useWhenAvailable(),
              (jobs) =>
                jobs ? jobs.map((job) => ({ id: job.id, text: job.name })) : [],
              ReadonlyArray.map((job) => (
                <Option key={job.id} value={job.id}>
                  {job.text}
                </Option>
              ))
            )}
          </AntSelect>
        </Container>
      </Form.Field.Vertical>
    );
  };
}

export namespace WorkCodes {
  export const Select = ({
    job,
    workCodes,
    onSelect,
    disabled = false,
  }: {
    readonly job: Data.Reference.Reference | undefined;
    readonly workCodes: readonly Data.Reference.Reference[] | undefined;
    readonly onSelect: (workCodes: readonly Data.Reference.Reference[] | undefined) => void;
    readonly disabled: boolean;
  }) => {
    const { enableMultipleWorkCodesOnElogsTimeEntry } = useFlags()

    const { Option } = AntSelect;
    const workCodesOptions = Remote.toUndefined(WC.useFromJob(job)) ?? [];
    return (
      <Form.Field.Vertical names={["workCode"]} label="Work code">
        <Container>
          <AntSelect
            mode={enableMultipleWorkCodesOnElogsTimeEntry ? 'multiple' : undefined}
            style={{ width: "100%" }}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={disabled || !workCodesOptions || workCodesOptions.length < 1}
            value={workCodes?.map(({ id }) => id)}
            placeholder="Select a work code..."
            onChange={(value) => {
              if (enableMultipleWorkCodesOnElogsTimeEntry) {
                return value && value.length ? onSelect( value.map(id => ({ id })) ) : onSelect(undefined)
              } else {
                return value ? onSelect([{ id: `${value}` }]) : onSelect(undefined)
              }
            }
              
            }
          >
            {pipe(
              workCodesOptions,
              ReadonlyArray.map((workCode) => ({
                id: workCode.id,
                text: WC.toDescription(workCode),
              })),
              ReadonlyArray.map((workCode) => (
                <Option key={workCode.id} value={workCode.id}>
                  {workCode.text}
                </Option>
              ))
            )}
          </AntSelect>
        </Container>
      </Form.Field.Vertical>
    );
  };
}

export namespace WorkOrder {
  export const Select = ({
    branch,
    revision,
    workOrder,
    onSelect,
    disabled = false,
  }: Partial<Branch> & {
    readonly revision: Log.Event.Event | Log.Suggestion.Suggestion;
    readonly workOrder: Data.Reference.Reference | undefined;
    readonly onSelect: (
      workOrder: Data.Reference.Reference | undefined
    ) => void;
    readonly disabled: boolean;
  }) => {
    const { Option } = AntSelect;
    return (
      <Form.Field.Vertical names={["workOrder"]} label="Work order">
        {!Log.Suggestion.canEditWork(revision) ? (
          // `canEditWork` is false for exactly one status, OFF, so this branch
          // is the off-duty case. Work cannot be performed off duty, so there
          // is nothing to display and nothing to look up — this used to render
          // whichever time entry overlapped the revision, which is the same
          // misreport sc-562324 removed from the table and the CSV export.
          <Placeholder />
        ) : (
          <Container>
            <AntSelect
              style={{ width: "100%" }}
              showSearch
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              disabled={disabled}
              value={workOrder?.id}
              placeholder="Select a work order..."
              onChange={(value) =>
                value ? onSelect({ id: `${value}` }) : onSelect(undefined)
              }
            >
              {pipe(
                WorkOrders.use(branch),
                Remote.map(({ workOrders }) => workOrders),
                Remote.getOrElseW(() => []),
                ReadonlyArray.sort(
                  Order.fromCompare<GenericWorkOrder>((a, b) =>
                    Order.optionally(Time.DateTime.compare)(
                      a.workOrder.submitted,
                      b.workOrder.submitted
                    )
                  )
                ),
                ReadonlyArray.map(({ workOrder }) => (
                  <Option key={workOrder.id} value={workOrder.id}>
                    {GenericWorkOrder.toString({ workOrder })}
                  </Option>
                ))
              )}
            </AntSelect>
          </Container>
        )}
      </Form.Field.Vertical>
    );
  };

  const Placeholder = () => <TextInput readOnly placeholder="Not Available" />;
}

export namespace Notes {
  export const Input = ({
    notes,
    errors,
    onChange,
  }: {
    readonly notes: string;
    readonly errors?: Form.Field.ValidationErrors<Fields>;
    readonly onChange: (notes: string) => void;
  }) => {
    const [note, setNotes] = useDebounceTextInput(notes, onChange);

    return (
      <Form.Field.Vertical
        label="Notes"
        names={["notes"]}
        isRequired
        errors={errors}
      >
        <TextInput
          required
          placeholder="Add a note..."
          value={note}
          onChange={({ target: { value: notes } }) => setNotes(notes)}
          onBlur={({ target: { value: notes } }) => onChange(notes)}
        />
      </Form.Field.Vertical>
    );
  };
}
export namespace Validation {
  type Errors = Form.Field.ValidationErrors<Fields>;

  export const defaultErrors = (): Errors => ({
    driverStatus: undefined,
    startTime: undefined,
    endTime: undefined,
    location: undefined,
    workOrder: undefined,
    workCodes: undefined,
    job: undefined,
    notes: undefined,
  });

  const isStartTimeValid = (data: Partial<State>): boolean =>
    !!data.startTime &&
    !!data.endTime &&
    Time.DateTime.isBefore(data.startTime)(data.endTime);

  export const validateStartTime = (
    data: Partial<State>,
    errors: Errors | undefined,
    updateErrors: React.Dispatch<React.SetStateAction<Errors>>
  ): boolean => {
    if (errors) {
      if (!isStartTimeValid(data)) {
        updateErrors({
          ...errors,
          ...{ startTime: "Start time must be before end time" },
        });
        return false;
      } else if (errors.startTime) {
        updateErrors({ ...errors, ...{ startTime: undefined } });
      }
    }
    return true;
  };

  const isEndTimeValid = (data: Partial<State>): boolean =>
    !!data.startTime &&
    !!data.endTime &&
    Time.DateTime.isAfter(data.endTime)(data.startTime);

  export const validateEndTime = (
    data: Partial<State>,
    errors: Errors | undefined,
    updateErrors: React.Dispatch<React.SetStateAction<Errors>>
  ): boolean => {
    if (errors) {
      if (!isEndTimeValid(data)) {
        updateErrors({
          ...errors,
          ...{ endTime: "End time must be after start time" },
        });
        return false;
      } else if (errors.endTime) {
        updateErrors({ ...errors, ...{ endTime: undefined } });
      }
    }
    return true;
  };

  const isLocationValid = (data: Partial<State>): boolean =>
    !!data.location && data.location.length > 0;

  export const validateLocation = (
    data: Partial<State>,
    errors: Errors | undefined,
    updateErrors: React.Dispatch<React.SetStateAction<Errors>>
  ): boolean => {
    if (errors) {
      if (!isLocationValid(data)) {
        updateErrors({ ...errors, ...{ location: "Location is required" } });
        return false;
      } else if (errors.location) {
        updateErrors({ ...errors, ...{ location: undefined } });
      }
    }
    return true;
  };

  const getNotesErrorMessage = (data: Partial<State>): string =>
  {
    if (!data.notes || data.notes.length == 0) {
      return "Notes are required";
    }
    else if (!!data.notes && data.notes.length > 1000) {
      return "Notes must be 1,000 characters or less"
    }
    else {
      return "";
    }
  }

  const isNotesValid = (data: Partial<State>): boolean =>
    !getNotesErrorMessage(data);

  export const validateNotes = (
    data: Partial<State>,
    errors: Errors | undefined,
    updateErrors: React.Dispatch<React.SetStateAction<Errors>>
  ): boolean => {
    if (errors) {
      const errorMessage = getNotesErrorMessage(data);
      if (errorMessage) {
        updateErrors({ ...errors, ...{ notes: errorMessage } });
        return false;
      } else if (errors.notes) {
        updateErrors({ ...errors, ...{ notes: undefined } });
      }
    }
    return true;
  };

  export const validateField = (
    field: Fields,
    data: Partial<State>,
    errors: Errors,
    updateErrors: React.Dispatch<React.SetStateAction<Errors>>
  ): boolean => {
    switch (field) {
      case "endTime":
        return validateEndTime(data, errors, updateErrors);
      case "location":
        return validateLocation(data, errors, updateErrors);
      case "notes":
        return validateNotes(data, errors, updateErrors);
      case "startTime":
        return validateStartTime(data, errors, updateErrors);
      default:
        return true;
    }
  };

  export const validateForm = (
    data: Partial<State>,
    errors: Errors | undefined,
    updateErrors: React.Dispatch<React.SetStateAction<Errors>>
  ): boolean => {
    const locationIsValid = validateLocation(data, errors, updateErrors);
    const notesIsValid = validateNotes(data, errors, updateErrors);
    const startTimeValid = validateStartTime(data, errors, updateErrors);
    const endTimeValid = validateEndTime(data, errors, updateErrors);
    return locationIsValid && notesIsValid && startTimeValid && endTimeValid;
  };

  export const isFormValid = (data: Partial<State>): boolean => {
    return (
      isLocationValid(data) &&
      isNotesValid(data) &&
      isStartTimeValid(data) &&
      isEndTimeValid(data)
    );
  };
}

export const useDebounceTextInput = (
  initialValue: string,
  callback: (value: string) => void,
  delay = Time.Duration.milliseconds(400)
): readonly [string, (value: string) => void] => {
  const [value, setValue] = useState(initialValue);

  const debounce = useDebounceCallback((value: string) => {
    callback(value);
  }, delay.duration);

  const capture = (value: string) => {
    setValue(value);
    debounce(value);
  };

  return [value, capture];
};

export const Container = styled(Layout.Block.Component)`
  display: flex;
  justify-content: stretch;

  & .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    width: 100%;
    padding: 6px;
    min-height: 40px;
    border-radius: 4px;
    border-color: ${Color.veryWhiteGrey};
  }
`;
